import { useTranslationTyped } from 'i18n/i18n'

const A11Y_STATEMENT_URL =
  'https://www.breakoutlearning.com/legal/accessibility-statement'

const SKIP_LINK_CLASSNAME =
  'skip-main ml-2 mt-2 flex min-h-16 min-w-52 items-center justify-center rounded-2xl bg-fixed-accent-color px-5 py-1 text-label-large outline-fixed-accent-color'

// After the skip link is activated, drop the leftover `#main` hash from the URL
// so it doesn't linger in the address bar / history.
function stripHash() {
  setTimeout(() => {
    history.replaceState(null, '', ' ')
  }, 16)
}

/**
 * Header skip links ("Skip to content" and "Accessibility statement"). These
 * only make sense on pages that render the main navigation, so this component
 * is rendered statically by TwoColumnSubRouter alongside the nav rather than
 * toggled on/off globally. The links are positioned offscreen (see
 * `a.skip-main` in index.css) and revealed on focus, so they are the first
 * focusable elements a keyboard user reaches.
 */
export function SkipLinks() {
  const { tt } = useTranslationTyped()

  const onSkipKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    // When a dialog is open, the page's main is renamed to `#main-disabled` and
    // the dialog backdrop becomes `#main` (see DialogMount). Force the jump into
    // `#main` so keyboard focus can't tab out into the inert background.
    if (e.key === 'Tab' && document.getElementById('main-disabled')) {
      e.preventDefault()
      e.currentTarget.click()
      return
    }

    // Links normally activate on Enter; map Space to click as well to avoid the
    // default page-scroll behavior on Space.
    if (e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
  }

  const onSkipClick = () => {
    const main = document.getElementById('main')
    if (main instanceof HTMLElement) {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1')
      main.focus()
    }
    stripHash()
  }

  return (
    <header>
      <a
        id="skip-main"
        className={SKIP_LINK_CLASSNAME}
        href="#main"
        onClick={onSkipClick}
        onKeyDown={onSkipKeyDown}
      >
        {tt.navigation.skip_to_content()}
      </a>
      <a
        id="a11y-statement-link"
        className={SKIP_LINK_CLASSNAME}
        href={A11Y_STATEMENT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {tt.navigation.accessibility_statement_link()}
      </a>
    </header>
  )
}
